import { useCallback, useRef } from 'react'

import { useInfiniteQuery } from '@tanstack/react-query'

import axios from 'axios'

const PER_PAGE = 10

function useGetLibraryItems(isLoggedIn, keywords) {
  const normalizedKeywords = (keywords ?? '').trim()
  const shouldUseKeywords = normalizedKeywords.length > 0

  // Only the search/keyword-change path aborts an in-flight request (below).
  // We intentionally do NOT abort on unmount: collapsing the sidebar (e.g. the
  // mobile initial-panel behavior) unmounts LibraryContent mid-fetch, and an
  // abort there both shows a "canceled" request and - because the catch resolves
  // to empty data - poisons the 5-minute-stale React Query cache, leaving the
  // library empty on reopen. Letting the request finish caches the real data.
  const abortControllerRef = useRef(null)
  const lastKeywordsRef = useRef(normalizedKeywords)

  const {
    data,
    hasNextPage: queryHasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isInitialLoading,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: shouldUseKeywords
      ? ['libraryItems', 'sessions', normalizedKeywords]
      : ['libraryItems', 'sessions'],
    queryFn: async ({ pageParam = 1 }) => {
      if (lastKeywordsRef.current !== normalizedKeywords) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort()
        }
        lastKeywordsRef.current = normalizedKeywords
      }

      const controller = new AbortController()
      abortControllerRef.current = controller

      try {
        const params = { page: pageParam, per_page: PER_PAGE }
        if (shouldUseKeywords) {
          params.keywords = normalizedKeywords
        }

        const { data: responseData } = await axios.get('/api/internal/generator/sessions', {
          params,
          signal: controller.signal,
        })

        return {
          items: responseData.data || [],
          meta: responseData.meta || {},
        }
      } catch (caughtError) {
        const isAborted = axios.isCancel(caughtError)
          || caughtError?.code === 'ERR_CANCELED'
          || caughtError?.name === 'CanceledError'

        if (isAborted) {
          return { items: [], meta: {} }
        }

        throw caughtError
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      const { currentPage, totalPages } = lastPage.meta
      return currentPage < totalPages ? allPages.length + 1 : undefined
    },
    initialPageParam: 1,
    enabled: isLoggedIn === true,
  })

  const items = data?.pages?.flatMap((page) => page.items) ?? []
  const hasNextPage = queryHasNextPage ?? false
  const meta = data?.pages?.slice(-1)[0]?.meta

  const handleLoadMore = useCallback(() => {
    fetchNextPage()
  }, [fetchNextPage])

  return {
    items,
    hasNextPage,
    handleLoadMore,
    isFetchingNextPage,
    isLoading: isInitialLoading,
    isError,
    error,
    meta,
  }
}

export default useGetLibraryItems
